import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AgentProfileDto,
  AgentUserPublicDto,
  GouvDvfDataDto,
  PropertyApplicationDto,
  PropertyApplicationPendingActionType,
  PropertyApplicationService,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { AlertSnackBarService, LuxonDatePipe } from '@workspace/ng-aura';
import { map, tap } from 'rxjs';
import {
  DiscussionProfileDialogComponent,
  DiscussionProfileDialogData,
} from '../../../discussion/components/discussion-profile-dialog/discussion-profile-dialog.component';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';

export type ApplicationFilterTab = 'active' | 'cancelled' | 'all';

export interface ComparisonSlot {
  index: number;
  application: PropertyApplicationDto | null;
}

export interface StatusBadgeConfig {
  label: string;
  badgeClass: string;
  icon: string;
  hint: string;
}

export interface PendingActionNotice {
  label: string;
  isOwnerAction: boolean;
}

@Component({
  selector: 'etxe-property-applications-owner-comparison',
  imports: [
    CommonModule,
    DecimalPipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    LuxonDatePipe,
  ],
  templateUrl: './property-applications-owner-comparison.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxePropertyApplicationsOwnerComparisonComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly property = input.required<PropertyDto>();
  readonly applications = input<PropertyApplicationDto[]>([]);
  readonly isLoading = input<boolean>(false);

  readonly applicationSelected = output<PropertyApplicationDto>();
  readonly refresh = output<void>();

  readonly MAX_SLOTS = 5;

  readonly sellingPriceEuros = computed<number | null>(() => {
    const cents = this.property().financials?.sellingPriceCents;
    return cents ? cents / 100 : null;
  });

  readonly surfaceHabitable = computed<number | null>(() => {
    const s = this.property().keyInfo?.surface?.habitable;
    return s && s > 0 ? s : null;
  });

  readonly dvfData = computed<GouvDvfDataDto | null>(() => {
    return this.property().geospatial?.dvf ?? null;
  });

  getAgentPricePerM2(app: PropertyApplicationDto): number | null {
    const price = this.getRecommendedPrice(app);
    const surface = this.surfaceHabitable();
    if (!price || !surface) return null;
    return Math.round(price / surface);
  }

  readonly acceptedApplication = computed<PropertyApplicationDto | null>(() => {
    return this.applications().find((app) => app.status === PropertyApplicationStatus.ACCEPTED) || null;
  });

  readonly activeApplications = computed<PropertyApplicationDto[]>(() => {
    return this.applications().filter(
      (app) =>
        app.status !== PropertyApplicationStatus.CANCELLED &&
        app.status !== PropertyApplicationStatus.REJECTED &&
        app.status !== PropertyApplicationStatus.EXPIRED,
    );
  });

  readonly activeApplicationsCount = computed<number>(() => {
    return this.activeApplications().length;
  });

  readonly cancelledApplications = computed<PropertyApplicationDto[]>(() => {
    return this.applications().filter(
      (app) =>
        app.status === PropertyApplicationStatus.CANCELLED ||
        app.status === PropertyApplicationStatus.REJECTED ||
        app.status === PropertyApplicationStatus.EXPIRED,
    );
  });

  readonly cancelledApplicationsCount = computed<number>(() => {
    return this.cancelledApplications().length;
  });

  readonly filterTab = signal<ApplicationFilterTab>('active');

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((params) => params.get('filter') as ApplicationFilterTab | null),
        tap((tab) => {
          if (tab === 'active' || tab === 'cancelled' || tab === 'all') {
            this.filterTab.set(tab);
          } else {
            this.filterTab.set('active');
          }
        }),
      )
      .subscribe();
  }

  setFilter(tab: ApplicationFilterTab): void {
    this.filterTab.set(tab);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filter: tab },
      queryParamsHandling: 'merge',
    });
  }

  readonly acceptedApplicationsCount = computed<number>(() => {
    return this.applications().filter(
      (app) =>
        app.status === PropertyApplicationStatus.VISIT_INVITED ||
        app.status === PropertyApplicationStatus.VISIT_CONFIRMED ||
        app.status === PropertyApplicationStatus.VISITED ||
        app.status === PropertyApplicationStatus.OFFER_SUBMITTED ||
        app.status === PropertyApplicationStatus.ACCEPTED,
    ).length;
  });

  readonly remainingSlotsCount = computed<number>(() => {
    return Math.max(0, this.MAX_SLOTS - this.acceptedApplicationsCount());
  });

  readonly slots = computed<ComparisonSlot[]>(() => {
    const apps = this.activeApplications();
    const result: ComparisonSlot[] = [];
    for (let i = 0; i < this.MAX_SLOTS; i++) {
      result.push({
        index: i,
        application: apps[i] || null,
      });
    }
    return result;
  });

  getAgentUser(app: PropertyApplicationDto): AgentUserPublicDto | null {
    const agent = app.agent;
    if (typeof agent === 'object' && agent !== null && 'user' in agent) {
      return typeof agent.user === 'object' && agent.user !== null
        ? (agent.user as AgentUserPublicDto)
        : null;
    }
    return null;
  }

  getAgentName(app: PropertyApplicationDto): string {
    const user = this.getAgentUser(app);
    const first = user?.identity?.firstName?.trim();
    const last = user?.identity?.lastName?.trim();
    if (first || last) {
      return `${first || ''} ${last || ''}`.trim();
    }
    const agent = typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
    if (agent?.activity?.network) {
      return `Agent ${agent.activity.network}`;
    }
    return 'Agent immobilier';
  }

  getAgentInitials(app: PropertyApplicationDto): string {
    const user = this.getAgentUser(app);
    const first = user?.identity?.firstName?.trim()?.[0] || '';
    const last = user?.identity?.lastName?.trim()?.[0] || '';
    if (first || last) {
      return (first + last).toUpperCase();
    }
    const name = this.getAgentName(app);
    return name.charAt(0).toUpperCase() || 'AG';
  }

  getAgentAvatar(app: PropertyApplicationDto): string | null {
    const avatar = this.getAgentUser(app)?.identity?.avatar;
    if (typeof avatar === 'string') return avatar;
    return avatar?.url ?? null;
  }

  openAgentProfileModal(app: PropertyApplicationDto, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    const agent = typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
    const agentId = agent?._id ? agent._id.toString() : typeof app.agent === 'string' ? app.agent : '';
    if (!agentId) return;

    const prop = this.property();
    this.dialog.open<DiscussionProfileDialogComponent, DiscussionProfileDialogData>(
      DiscussionProfileDialogComponent,
      {
        width: '100%',
        maxWidth: '760px',
        maxHeight: '90vh',
        autoFocus: false,
        data: {
          participantId: agentId,
          participantType: 'agent',
          participantName: this.getAgentName(app),
          participantAvatarUrl: this.getAgentAvatar(app),
          propertyId: prop?._id ? prop._id.toString() : undefined,
          agentProfile: agent ?? undefined,
        },
      },
    );
  }

  getAgentNetwork(app: PropertyApplicationDto): string {
    return this.getAgentAffiliation(app).displayBadge;
  }

  getAgentAffiliation(app: PropertyApplicationDto): {
    network?: string;
    agencyName?: string;
    typeLabel: string;
    isTitulaire: boolean;
    displayBadge: string;
    hasBoth: boolean;
  } {
    const agent = typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
    const network = agent?.activity?.network?.trim();
    const agencyName = agent?.cards?.activeCard?.companyName?.trim();
    const isTitulaire = agent?.type === 'titulaire';
    const typeLabel = isTitulaire ? 'Titulaire de carte T' : 'Mandataire indépendant';

    const hasBoth = Boolean(network && agencyName && network.toLowerCase() !== agencyName.toLowerCase());

    let displayBadge: string;
    if (hasBoth) {
      displayBadge = `${network} • ${agencyName}`;
    } else if (network) {
      displayBadge = network;
    } else if (agencyName) {
      displayBadge = agencyName;
    } else {
      displayBadge = typeLabel;
    }

    return {
      network,
      agencyName,
      typeLabel,
      isTitulaire,
      displayBadge,
      hasBoth,
    };
  }

  getAgentCity(app: PropertyApplicationDto): string | null {
    const agent = typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
    return agent?.work?.officeAddress?.city || agent?.intervention?.preferredCities?.[0]?.name || null;
  }

  getAgentCardNumber(app: PropertyApplicationDto): string | null {
    const agent = typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
    return agent?.cards?.activeCard?.number ?? null;
  }

  getCommissionRate(app: PropertyApplicationDto): number {
    return app.engagement?.finalCommissionRate ?? app.engagement?.commissionRate ?? 0;
  }

  hasFinalOffer(app: PropertyApplicationDto): boolean {
    return (
      app.engagement?.finalCommissionRate != null ||
      app.status === PropertyApplicationStatus.OFFER_SUBMITTED ||
      app.status === PropertyApplicationStatus.ACCEPTED
    );
  }

  getCommissionEuros(app: PropertyApplicationDto): number | null {
    const refPrice = app.engagement?.valuation?.recommendedPrice ?? this.sellingPriceEuros();
    const rate = this.getCommissionRate(app);
    if (!refPrice || !rate) return null;
    return Math.round((refPrice * rate) / 100);
  }

  getRecommendedPrice(app: PropertyApplicationDto): number | null {
    return app.engagement?.valuation?.recommendedPrice ?? null;
  }

  getPriceDifferencePercent(app: PropertyApplicationDto): number | null {
    const recommended = this.getRecommendedPrice(app);
    const selling = this.sellingPriceEuros();
    if (!recommended || !selling) return null;
    const diff = ((recommended - selling) / selling) * 100;
    return Number(diff.toFixed(1));
  }

  getAttractivePrice(app: PropertyApplicationDto): number | null {
    return app.engagement?.valuation?.attractivePrice ?? null;
  }

  getHeartStrokePrice(app: PropertyApplicationDto): number | null {
    return app.engagement?.valuation?.heartStrokePrice ?? null;
  }

  getValuationPresentation(app: PropertyApplicationDto): string | null {
    return app.engagement?.valuation?.generalPresentation?.trim() || null;
  }

  getServices(app: PropertyApplicationDto): PropertyApplicationService[] {
    return app.engagement?.services || [];
  }

  getServiceLabel(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationService(service);
  }

  getServiceIcon(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationServiceIcon(service);
  }

  getServiceDescription(service: PropertyApplicationService): string {
    return EtxeEnumsNiceLabelUtils.applicationServiceDescription(service);
  }

  getPendingActionNotice(app: PropertyApplicationDto): PendingActionNotice | null {
    const action = app.pendingAction;
    if (!action) return null;
    const isOwner = action.waitingFor === 'owner';
    switch (action.actionType) {
      case PropertyApplicationPendingActionType.VISIT_PROPOSAL:
        return {
          label: isOwner ? 'Proposition de visite à confirmer' : 'En attente d’une proposition de visite',
          isOwnerAction: isOwner,
        };
      case PropertyApplicationPendingActionType.VISIT_RESCHEDULE:
        return {
          label: isOwner ? 'Nouveau créneau de visite proposé' : 'Visite en cours de reprogrammation',
          isOwnerAction: isOwner,
        };
      case PropertyApplicationPendingActionType.FINAL_OFFER:
        return {
          label: isOwner ? 'Offre finale disponible' : 'En attente du chiffrage définitif de l’agent',
          isOwnerAction: isOwner,
        };
      case PropertyApplicationPendingActionType.MANDATE_SELECTION:
        return {
          label: isOwner ? 'Sélection du mandataire à finaliser' : 'Décision du propriétaire en cours',
          isOwnerAction: isOwner,
        };
      default:
        return null;
    }
  }

  openDiscussion(app: PropertyApplicationDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!app.discussion) {
      this.alertSnackbar.info({
        title: 'Discussion',
        message: 'La discussion avec cet agent sera initialisée dès votre premier message.',
      });
      return;
    }
    void this.router.navigate(['/messages', app.discussion]);
  }

  getStatusConfig(status: PropertyApplicationStatus): StatusBadgeConfig {
    switch (status) {
      case PropertyApplicationStatus.PENDING:
        return {
          label: 'Candidature reçue',
          badgeClass: 'bg-amber-2 text-amber-11 border-amber-5',
          icon: 'schedule',
          hint: 'En attente d’examen',
        };
      case PropertyApplicationStatus.VISIT_INVITED:
        return {
          label: 'Visite proposée',
          badgeClass: 'bg-sky-2 text-sky-11 border-sky-5',
          icon: 'calendar_month',
          hint: 'Créneau proposé',
        };
      case PropertyApplicationStatus.VISIT_CONFIRMED:
        return {
          label: 'Visite confirmée',
          badgeClass: 'bg-indigo-2 text-indigo-11 border-indigo-5',
          icon: 'event_available',
          hint: 'RDV planifié',
        };
      case PropertyApplicationStatus.VISITED:
        return {
          label: 'Visite réalisée',
          badgeClass: 'bg-indigo-2 text-indigo-11 border-indigo-5',
          icon: 'check_circle',
          hint: 'En attente de l’offre finale',
        };
      case PropertyApplicationStatus.OFFER_SUBMITTED:
        return {
          label: 'Offre & avis remis',
          badgeClass: 'bg-purple-2 text-purple-11 border-purple-5',
          icon: 'price_check',
          hint: 'Prêt pour sélection',
        };
      case PropertyApplicationStatus.ACCEPTED:
        return {
          label: 'Mandat confié',
          badgeClass: 'bg-jade-2 text-jade-11 border-jade-5',
          icon: 'handshake',
          hint: 'Agent retenu',
        };
      case PropertyApplicationStatus.REJECTED:
        return {
          label: 'Non retenue',
          badgeClass: 'bg-neutral-3 text-neutral-11 border-neutral-6',
          icon: 'block',
          hint: 'Candidature déclinée par le propriétaire',
        };
      case PropertyApplicationStatus.CANCELLED:
        return {
          label: 'Candidature retirée',
          badgeClass: 'bg-red-2 text-red-11 border-red-5',
          icon: 'cancel',
          hint: 'L’agent s’est retiré de sa candidature',
        };
      case PropertyApplicationStatus.EXPIRED:
        return {
          label: 'Délai expiré',
          badgeClass: 'bg-amber-2 text-amber-11 border-amber-5',
          icon: 'timer_off',
          hint: 'Clôturée automatiquement après expiration',
        };
    }
  }

  onSelect(app: PropertyApplicationDto): void {
    this.applicationSelected.emit(app);
  }
}
